<script>
/**
 * GrupoComposer — espelha o composer do Atendimento, adaptado pra API-B.
 *   - "+" → menu (Documento / Foto/Vídeo / Localização) + formatação WhatsApp
 *   - Emoji picker
 *   - Anexo com preview e legenda
 *   - Áudio com gravação/pausa/onda animada
 *
 * Props: grupoId (id da linha em `grupo`), disabled
 * Envia direto:
 *   - texto:        POST /grupo/mensagem/:id           { texto }
 *   - mídia:        POST /grupo/midia/:id              multipart {file, tipo, caption}
 *   - áudio:        POST /grupo/audio/:id              multipart {file}
 *   - localização:  POST /grupo/mensagem/:id           { latitude, longitude, ... }  (back faz sendMessage)
 */
import axios from 'axios';
import resolveUrl from '@/services/resolveUrl';

// Primeira URL http(s) do texto — usada pra montar a prévia ao vivo (WhatsApp Web).
const URL_RE = /\bhttps?:\/\/[^\s]+/i;

const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😅', '😊', '😇', '🙂', '😉', '😍', '😘', '🥰', '😎', '🤩', '🥳',
  '🤔', '🤨', '😐', '😶', '🙄', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😢', '😭', '😤', '😠', '😡',
  '👍', '👎', '👏', '🙌', '🤝', '👋', '🙏', '💪', '👌', '✌️', '🤞', '✊',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕', '✨', '🔥', '⭐', '🎉', '🎊', '🎁',
  '✅', '❌', '⚠️', '❗', '❓', '💯', '🆗', '🆘',
];

export default {
  name: 'GrupoComposer',
  props: {
    grupoId: { type: [Number, String], default: null },
    disabled: { type: Boolean, default: false },
  },
  emits: ['enviado', 'erro', 'abrir-enquete', 'abrir-midia-url', 'abrir-evento'],
  data() {
    return {
      texto: '',
      enviando: false,
      // Menu "+"
      mostrarMenuMais: false,
      // Anexos
      arquivoSelecionado: null,
      previewArquivo: null,
      tipoSelecionado: null,
      caption: '',
      // Áudio
      gravando: false,
      pausado: false,
      audioBlob: null,
      audioUrl: null,
      mediaRecorder: null,
      tempoGravacao: 0,
      timerGravacao: null,
      enviarAposStop: false,
      // Emoji
      mostrarEmoji: false,
      emojis: EMOJIS,
      // Localização
      enviandoLocalizacao: false,
      // Prévia de link ao vivo (composer), igual ao WhatsApp Web.
      linkPreview: null, // { url, title, description, siteName, image }
      linkPreviewLoading: false,
      linkPreviewUrl: null, // URL que está previewada/em busca
      linkPreviewDismissedUrl: null, // URL que o usuário fechou (não re-busca)
    };
  },
  computed: {
    tipoLabel() {
      return { imagem: 'Imagem', video: 'Vídeo', audio: 'Áudio', documento: 'Documento' }[this.tipoSelecionado] || 'Arquivo';
    },
    tamanhoLegivel() {
      const b = this.arquivoSelecionado ? this.arquivoSelecionado.size : 0;
      if (!b) return '';
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
      return `${(b / 1024 / 1024).toFixed(1)} MB`;
    },
    mostrarBotaoMicrofone() {
      return !this.texto.trim() && !this.gravando && !this.audioBlob;
    },
    emModoGravacao() {
      return this.gravando || this.pausado || Boolean(this.audioBlob);
    },
    duracaoFormatada() {
      const t = this.tempoGravacao;
      const mm = String(Math.floor(t / 60)).padStart(2, '0');
      const ss = String(t % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    },
  },
  watch: {
    texto() { this._agendarPreviewLink(); },
  },
  mounted() {
    document.addEventListener('click', this._onClickFora);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._onClickFora);
    if (this.timerGravacao) clearInterval(this.timerGravacao);
    if (this._linkTimer) clearTimeout(this._linkTimer);
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    if (this.previewArquivo) URL.revokeObjectURL(this.previewArquivo);
  },
  methods: {
    _focar() {
      if (this.$refs.textarea) this.$refs.textarea.focus();
    },
    _onClickFora(e) {
      if (this.mostrarMenuMais && this.$refs.menuMaisBox && !this.$refs.menuMaisBox.contains(e.target)) {
        this.mostrarMenuMais = false;
      }
      if (this.mostrarEmoji && this.$refs.emojiBox && !this.$refs.emojiBox.contains(e.target)) {
        this.mostrarEmoji = false;
      }
    },

    // ── Prévia de link ao vivo (WhatsApp Web) ──────────────────────────
    resolveAssetUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (/^(https?:|data:)/i.test(relativeUrl)) return relativeUrl;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativeUrl}`;
    },
    _urlDoTexto() {
      const m = String(this.texto || '').match(URL_RE);
      if (!m) return null;
      return m[0].replace(/[.,;:!?)\]]+$/, '');
    },
    _agendarPreviewLink() {
      const url = this._urlDoTexto();
      if (!url) { this._limparPreviewLink(); return; }
      if (url === this.linkPreviewDismissedUrl) return; // usuário fechou este
      if (url === this.linkPreviewUrl) return; // já temos/estamos buscando
      if (this._linkTimer) clearTimeout(this._linkTimer);
      this._linkTimer = setTimeout(() => this._buscarPreviewLink(url), 600);
    },
    async _buscarPreviewLink(url) {
      this.linkPreviewUrl = url;
      this.linkPreview = null;
      this.linkPreviewLoading = true;
      try {
        const { data } = await axios.get('/link/preview', { params: { url } });
        // Corrida: só aplica se ainda é a URL atual do texto e não foi fechada.
        if (this._urlDoTexto() !== url || url === this.linkPreviewDismissedUrl) return;
        this.linkPreview = data && (data.title || data.image) ? data : null;
      } catch (_e) {
        this.linkPreview = null;
      } finally {
        if (this.linkPreviewUrl === url) this.linkPreviewLoading = false;
      }
    },
    fecharPreviewLink() {
      this.linkPreviewDismissedUrl = this.linkPreviewUrl;
      this.linkPreview = null;
      this.linkPreviewLoading = false;
    },
    _limparPreviewLink() {
      if (this._linkTimer) { clearTimeout(this._linkTimer); this._linkTimer = null; }
      this.linkPreview = null;
      this.linkPreviewLoading = false;
      this.linkPreviewUrl = null;
      this.linkPreviewDismissedUrl = null;
    },

    // ── Texto ──────────────────────────────────────────────────────────
    async enviarTexto() {
      if (!this.texto.trim() || !this.grupoId || this.enviando) return;
      const texto = this.texto;
      this.enviando = true;
      try {
        await axios.post(`/grupo/mensagem/${this.grupoId}`, { texto });
        this.texto = '';
        this._limparPreviewLink();
        this.$emit('enviado', { tipo: 'texto', conteudo: texto });
      } catch (error) {
        this.$emit('erro', this._erroMsg(error));
      } finally {
        this.enviando = false;
        this.$nextTick(() => this._focar());
      }
    },
    onKeyDown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.enviarTexto();
      }
    },

    // ── Formatação WhatsApp ────────────────────────────────────────────
    formatar(prefixo, sufixo) {
      const el = this.$refs.textarea;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const sel = this.texto.substring(start, end);
      const novo = `${this.texto.substring(0, start)}${prefixo}${sel || 'texto'}${sufixo}${this.texto.substring(end)}`;
      this.texto = novo;
      this.$nextTick(() => {
        el.focus();
        el.setSelectionRange(start + prefixo.length, end + prefixo.length);
      });
    },
    aplicarFormatacao(prefixo, sufixo) {
      this.mostrarMenuMais = false;
      this.formatar(prefixo, sufixo);
    },

    // ── Emoji ──────────────────────────────────────────────────────────
    inserirEmoji(emoji) {
      const el = this.$refs.textarea;
      const pos = el ? el.selectionStart : this.texto.length;
      this.texto = `${this.texto.substring(0, pos)}${emoji}${this.texto.substring(pos)}`;
      this.mostrarEmoji = false;
      this.$nextTick(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(pos + emoji.length, pos + emoji.length);
        }
      });
    },

    // ── Menu "+" ações ─────────────────────────────────────────────────
    abrirAnexoArquivo() { this.mostrarMenuMais = false; this.$refs.inputArquivo.click(); },
    abrirAnexoImagem() { this.mostrarMenuMais = false; this.$refs.inputImagem.click(); },
    abrirCamera() { this.mostrarMenuMais = false; this.$refs.inputCamera.click(); },
    abrirAnexoAudio() { this.mostrarMenuMais = false; this.$refs.inputAudio.click(); },
    chamarLocalizacao() { this.mostrarMenuMais = false; this.enviarLocalizacao(); },
    chamarEnquete() { this.mostrarMenuMais = false; this.$emit('abrir-enquete'); },
    chamarEvento() { this.mostrarMenuMais = false; this.$emit('abrir-evento'); },
    chamarMidiaUrl() { this.mostrarMenuMais = false; this.$emit('abrir-midia-url'); },

    // ── Anexo ──────────────────────────────────────────────────────────
    onArquivoEscolhido(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.arquivoSelecionado = file;
      if (file.type.startsWith('image/')) this.tipoSelecionado = 'imagem';
      else if (file.type.startsWith('video/')) this.tipoSelecionado = 'video';
      else if (file.type.startsWith('audio/')) this.tipoSelecionado = 'audio';
      else this.tipoSelecionado = 'documento';
      if (this.tipoSelecionado === 'imagem' || this.tipoSelecionado === 'video') {
        this.previewArquivo = URL.createObjectURL(file);
      } else {
        this.previewArquivo = null;
      }
      this.caption = '';
      event.target.value = '';
    },
    cancelarArquivo() {
      if (this.previewArquivo) URL.revokeObjectURL(this.previewArquivo);
      this.arquivoSelecionado = null;
      this.previewArquivo = null;
      this.tipoSelecionado = null;
      this.caption = '';
    },
    async enviarArquivo() {
      if (!this.arquivoSelecionado || !this.grupoId) return;
      this.enviando = true;
      try {
        const form = new FormData();
        form.append('file', this.arquivoSelecionado);
        form.append('tipo', this.tipoSelecionado);
        if (this.caption) form.append('caption', this.caption);
        await axios.post(`/grupo/midia/${this.grupoId}`, form);
        this.cancelarArquivo();
        this.$emit('enviado', { tipo: this.tipoSelecionado });
      } catch (error) {
        this.$emit('erro', this._erroMsg(error));
      } finally {
        this.enviando = false;
      }
    },

    // ── Áudio (MediaRecorder) ──────────────────────────────────────────
    async iniciarGravacao() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const candidatos = [
          'audio/ogg;codecs=opus', 'audio/ogg',
          'audio/mp4;codecs=mp4a.40.2', 'audio/mp4',
          'audio/mpeg',
        ];
        let mimeType = '';
        for (const m of candidatos) {
          if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) {
            if (!mimeType) mimeType = m;
          }
        }
        const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        const chunks = [];
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        mr.onstop = () => {
          const tipoFinal = mr.mimeType || mimeType || 'audio/ogg';
          this.audioBlob = new Blob(chunks, { type: tipoFinal });
          this.audioUrl = URL.createObjectURL(this.audioBlob);
          stream.getTracks().forEach((t) => t.stop());
          if (this.enviarAposStop) {
            this.enviarAposStop = false;
            this.enviarAudio();
          }
        };
        mr.start();
        this.mediaRecorder = mr;
        this.gravando = true;
        this.tempoGravacao = 0;
        this.timerGravacao = setInterval(() => { this.tempoGravacao += 1; }, 1000);
      } catch (error) {
        this.$emit('erro', `Não foi possível acessar o microfone: ${error.message}`);
      }
    },
    pararGravacao() {
      if (this.mediaRecorder && this.gravando) this.mediaRecorder.stop();
      this.gravando = false;
      this.pausado = false;
      clearInterval(this.timerGravacao);
    },
    togglePausar() {
      if (!this.mediaRecorder || !this.gravando) return;
      if (this.pausado) {
        try { this.mediaRecorder.resume(); } catch (_e) { /* noop */ }
        this.pausado = false;
        this.timerGravacao = setInterval(() => { this.tempoGravacao += 1; }, 1000);
      } else {
        try { this.mediaRecorder.pause(); } catch (_e) { /* noop */ }
        this.pausado = true;
        clearInterval(this.timerGravacao);
      }
    },
    descartarGravacao() {
      const mr = this.mediaRecorder;
      if (mr && (this.gravando || this.pausado)) {
        this.enviarAposStop = false;
        mr.onstop = () => {
          try { mr.stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        };
        try { mr.stop(); } catch (_e) { /* noop */ }
      }
      this._limparGravacao();
    },
    finalizarEEnviarAudio() {
      if (this.gravando || this.pausado) {
        this.enviarAposStop = true;
        this.pararGravacao();
      } else if (this.audioBlob) {
        this.enviarAudio();
      }
    },
    _limparGravacao() {
      this.gravando = false;
      this.pausado = false;
      this.enviarAposStop = false;
      clearInterval(this.timerGravacao);
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioBlob = null;
      this.audioUrl = null;
      this.tempoGravacao = 0;
      if (this.mediaRecorder) {
        try { this.mediaRecorder.stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        this.mediaRecorder = null;
      }
    },
    async enviarAudio() {
      if (!this.audioBlob || !this.grupoId) return;
      this.enviando = true;
      try {
        const tipoBlob = (this.audioBlob.type || 'audio/ogg').split(';')[0];
        const ext = tipoBlob.includes('ogg') ? 'ogg'
          : tipoBlob.includes('mp4') ? 'm4a'
            : tipoBlob.includes('mpeg') ? 'mp3'
              : tipoBlob.includes('webm') ? 'webm'
                : 'ogg';
        const filename = `audio-${Date.now()}.${ext}`;
        const form = new FormData();
        form.append('file', this.audioBlob, filename);
        await axios.post(`/grupo/audio/${this.grupoId}`, form);
        this._limparGravacao();
        this.$emit('enviado', { tipo: 'audio' });
      } catch (error) {
        this.$emit('erro', this._erroMsg(error));
      } finally {
        this.enviando = false;
      }
    },

    // ── Localização ────────────────────────────────────────────────────
    async enviarLocalizacao() {
      if (!this.grupoId) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm('Compartilhar sua localização atual com o grupo?')) return;
      this.enviandoLocalizacao = true;
      try {
        const pos = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error('Geolocalização não suportada.'));
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
          return null;
        });
        await axios.post(`/grupo/mensagem/${this.grupoId}`, {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        this.$emit('enviado', { tipo: 'localizacao' });
      } catch (error) {
        this.$emit('erro', `Falha ao enviar localização: ${this._erroMsg(error)}`);
      } finally {
        this.enviandoLocalizacao = false;
      }
    },

    _erroMsg(error) {
      const data = error.response && error.response.data;
      return (data && (data.details || data.error)) || error.message;
    },
  },
};
</script>

<template>
  <div class="composer-wrapper border-top">
    <!-- Preview de arquivo selecionado -->
    <div v-if="arquivoSelecionado" class="preview-anexo">
      <div class="preview-anexo-card">
        <img v-if="tipoSelecionado === 'imagem'" :src="previewArquivo" class="preview-thumb" alt="preview" />
        <video v-else-if="tipoSelecionado === 'video'" :src="previewArquivo" class="preview-thumb" controls></video>
        <div v-else class="preview-thumb preview-thumb--file">
          <i :class="tipoSelecionado === 'audio' ? 'ri-music-2-line' : 'ri-file-text-line'"></i>
        </div>
        <div class="preview-anexo-body">
          <div class="preview-anexo-nome text-truncate">{{ arquivoSelecionado.name }}</div>
          <div class="preview-anexo-meta">{{ tipoLabel }} · {{ tamanhoLegivel }}</div>
          <input
            v-model="caption"
            type="text"
            class="preview-anexo-legenda"
            placeholder="Adicione uma legenda…"
            @keydown.enter="enviarArquivo"
          />
        </div>
        <button type="button" class="preview-anexo-fechar" title="Cancelar" @click="cancelarArquivo">
          <i class="ri-close-line"></i>
        </button>
      </div>
      <div class="preview-anexo-acoes">
        <button type="button" class="btn-enviar-anexo" :disabled="enviando" @click="enviarArquivo">
          <i v-if="enviando" class="ri-loader-4-line spin"></i>
          <i v-else class="ri-send-plane-2-fill"></i>
          {{ enviando ? 'Enviando…' : 'Enviar' }}
        </button>
      </div>
    </div>

    <!-- Emoji picker -->
    <div v-if="mostrarEmoji" ref="emojiBox" class="emoji-picker">
      <button
        v-for="(e, i) in emojis"
        :key="i"
        type="button"
        class="emoji-btn"
        @click="inserirEmoji(e)"
      >{{ e }}</button>
    </div>

    <!-- Prévia de link ao vivo (igual WhatsApp Web) -->
    <div
      v-if="!emModoGravacao && !arquivoSelecionado && (linkPreviewLoading || linkPreview)"
      class="composer-linkprev"
    >
      <div v-if="linkPreviewLoading" class="composer-linkprev-load">
        <i class="ri-loader-4-line spin"></i>
        <span>Carregando prévia do link…</span>
      </div>
      <template v-else>
        <img
          v-if="linkPreview.image"
          :src="resolveAssetUrl(linkPreview.image)"
          class="composer-linkprev-thumb"
          alt=""
          @error="$event.target.remove()"
        />
        <div class="composer-linkprev-body">
          <div v-if="linkPreview.title" class="composer-linkprev-title text-truncate">{{ linkPreview.title }}</div>
          <div v-if="linkPreview.description" class="composer-linkprev-desc">{{ linkPreview.description }}</div>
          <div class="composer-linkprev-url text-truncate">{{ linkPreview.siteName || linkPreview.url }}</div>
        </div>
      </template>
      <button type="button" class="composer-linkprev-close" title="Remover prévia" @click="fecharPreviewLink">
        <i class="ri-close-line"></i>
      </button>
    </div>

    <!-- Barra principal -->
    <div v-if="!emModoGravacao" class="composer composer-wa">
      <!-- "+" menu -->
      <div class="composer-action-wrapper">
        <button
          type="button"
          class="composer-action-btn"
          :disabled="disabled"
          title="Anexar / formatar"
          @click.stop="mostrarMenuMais = !mostrarMenuMais"
        >
          <i :class="mostrarMenuMais ? 'ri-close-line' : 'ri-add-line'"></i>
        </button>
        <div v-if="mostrarMenuMais" ref="menuMaisBox" class="composer-popup composer-popup--menu" @click.stop>
          <button type="button" class="popup-item" @click="abrirAnexoArquivo">
            <span class="popup-ico" style="color:#7c5cff"><i class="ri-file-text-line"></i></span>
            <span>Documento</span>
          </button>
          <button type="button" class="popup-item" @click="abrirAnexoImagem">
            <span class="popup-ico" style="color:#1fa3ff"><i class="ri-image-2-line"></i></span>
            <span>Fotos e vídeos</span>
          </button>
          <button type="button" class="popup-item" @click="abrirCamera">
            <span class="popup-ico" style="color:#ff4d7d"><i class="ri-camera-line"></i></span>
            <span>Câmera</span>
          </button>
          <button type="button" class="popup-item" @click="abrirAnexoAudio">
            <span class="popup-ico" style="color:#ff8c1a"><i class="ri-headphone-line"></i></span>
            <span>Áudio</span>
          </button>
          <button type="button" class="popup-item" @click="chamarEnquete">
            <span class="popup-ico" style="color:#f2b705"><i class="ri-bar-chart-horizontal-line"></i></span>
            <span>Enquete</span>
          </button>
          <button type="button" class="popup-item" @click="chamarEvento">
            <span class="popup-ico" style="color:#e0559b"><i class="ri-calendar-event-line"></i></span>
            <span>Evento</span>
          </button>
          <button type="button" class="popup-item" :disabled="enviandoLocalizacao" @click="chamarLocalizacao">
            <span class="popup-ico" style="color:#12b76a"><i class="ri-map-pin-line"></i></span>
            <span>Localização</span>
          </button>
          <button type="button" class="popup-item" @click="chamarMidiaUrl">
            <span class="popup-ico" style="color:#00b3a4"><i class="ri-link"></i></span>
            <span>Enviar mídia por URL</span>
          </button>
          <div class="popup-divisor"></div>
          <div class="popup-formatos">
            <button type="button" class="popup-formato" title="Negrito (*texto*)" @click="aplicarFormatacao('*', '*')">
              <i class="ri-bold"></i>
            </button>
            <button type="button" class="popup-formato" title="Itálico (_texto_)" @click="aplicarFormatacao('_', '_')">
              <i class="ri-italic"></i>
            </button>
            <button type="button" class="popup-formato" title="Tachado (~texto~)" @click="aplicarFormatacao('~', '~')">
              <i class="ri-strikethrough"></i>
            </button>
            <button type="button" class="popup-formato" title="Monoespaçado (```texto```)" @click="aplicarFormatacao('```', '```')">
              <i class="ri-code-line"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Emoji -->
      <button type="button" class="composer-action-btn" :disabled="disabled" title="Emoji" @click.stop="mostrarEmoji = !mostrarEmoji">
        <i class="ri-emotion-happy-line"></i>
      </button>

      <!-- Inputs ocultos -->
      <input ref="inputArquivo" type="file" class="d-none" @change="onArquivoEscolhido" />
      <input ref="inputImagem" type="file" accept="image/*,video/*" class="d-none" @change="onArquivoEscolhido" />
      <input ref="inputCamera" type="file" accept="image/*" capture="environment" class="d-none" @change="onArquivoEscolhido" />
      <input ref="inputAudio" type="file" accept="audio/*" class="d-none" @change="onArquivoEscolhido" />

      <!-- Texto -->
      <textarea
        ref="textarea"
        v-model="texto"
        class="composer-input"
        rows="1"
        placeholder="Digite uma mensagem..."
        :disabled="disabled || enviando"
        @keydown="onKeyDown"
      ></textarea>

      <!-- Microfone / Enviar -->
      <button v-if="mostrarBotaoMicrofone" type="button" class="btn composer-send-btn composer-mic-btn" :disabled="disabled || enviando" title="Gravar áudio" @click="iniciarGravacao">
        <i class="ri-mic-line"></i>
      </button>
      <button v-else type="button" class="btn composer-send-btn" :disabled="enviando || !texto.trim()" title="Enviar" @click="enviarTexto">
        <i v-if="enviando" class="ri-loader-4-line spin"></i>
        <i v-else class="ri-send-plane-2-line"></i>
      </button>
    </div>

    <!-- Barra de gravação -->
    <div v-else class="recording-bar">
      <button type="button" class="recording-btn recording-btn--ghost" title="Descartar" :disabled="enviando" @click="descartarGravacao">
        <i class="ri-delete-bin-line"></i>
      </button>

      <div class="recording-meta">
        <span class="rec-dot" :class="{ 'rec-dot--pausado': pausado || !gravando }"></span>
        <span class="recording-time">{{ duracaoFormatada }}</span>
      </div>

      <div class="recording-waveform" :class="{ 'recording-waveform--ativo': gravando && !pausado }" aria-hidden="true">
        <span v-for="n in 36" :key="n" :style="{ animationDelay: `${(n % 6) * 0.1}s` }"></span>
      </div>

      <button v-if="gravando" type="button" class="recording-btn recording-btn--ghost" :title="pausado ? 'Retomar' : 'Pausar'" @click="togglePausar">
        <i :class="pausado ? 'ri-play-fill' : 'ri-pause-fill'"></i>
      </button>

      <button type="button" class="recording-btn recording-btn--send" :disabled="enviando" title="Enviar áudio" @click="finalizarEEnviarAudio">
        <i v-if="enviando" class="ri-loader-4-line spin"></i>
        <i v-else class="ri-send-plane-2-line"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.composer-wrapper { background: var(--vz-body-bg); border-color: var(--vz-border-color); position: relative; padding-bottom: env(safe-area-inset-bottom, 0px); }
.composer { background: var(--vz-body-bg); }

.composer-wa { display: flex; align-items: flex-end; gap: 0.35rem; padding: 0.5rem 0.75rem; background: var(--vz-body-bg); }
.composer-action-wrapper { position: relative; display: inline-flex; }
.composer-action-btn {
  width: 40px; height: 40px;
  border: 0; background: transparent;
  color: var(--vz-secondary-color);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.35rem; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s, color 0.15s; padding: 0;
}
.composer-action-btn:hover:not(:disabled) { background: var(--vz-tertiary-bg); color: var(--vz-primary); }
.composer-action-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.composer-input {
  flex: 1; min-width: 0; resize: none; max-height: 120px;
  border: 1px solid var(--vz-border-color); border-radius: 22px;
  padding: 0.55rem 1rem; font-size: 0.9rem; line-height: 1.4;
  background: #fff; color: var(--vz-body-color);
  outline: none; transition: border-color 0.15s, box-shadow 0.15s;
}
.composer-input:focus { border-color: #c9ced4; box-shadow: none; }
.composer-input:disabled { background: var(--vz-tertiary-bg); color: var(--vz-secondary-color); cursor: not-allowed; }

.composer-popup {
  position: absolute; bottom: calc(100% + 8px); left: 0;
  background: var(--vz-body-bg); border: 1px solid var(--vz-border-color); border-radius: 0.85rem;
  padding: 0.4rem; box-shadow: 0 10px 34px rgba(0,0,0,0.22); z-index: 6; min-width: 236px;
}
.popup-item {
  display: flex; align-items: center; gap: 0.75rem; width: 100%;
  border: 0; background: transparent; color: var(--vz-body-color);
  padding: 0.5rem 0.6rem; border-radius: 0.55rem; cursor: pointer;
  font-size: 0.9rem; text-align: left; transition: background 0.12s;
}
.popup-item:hover:not(:disabled) { background: var(--vz-tertiary-bg); }
.popup-item:disabled { opacity: 0.55; cursor: not-allowed; }
/* Ícone colorido com círculo tint (color-mix usa a cor inline do próprio ícone). */
.popup-ico {
  width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; font-size: 1.2rem;
  background: color-mix(in srgb, currentColor 16%, transparent);
}
.popup-divisor { height: 1px; background: var(--vz-border-color); margin: 0.4rem 0.3rem; }
.popup-formatos { display: flex; gap: 2px; padding: 0.2rem 0.3rem; }
.popup-formato {
  flex: 1; border: 0; background: transparent;
  color: var(--vz-secondary-color); font-size: 1rem;
  padding: 0.45rem 0; border-radius: 0.35rem; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.popup-formato:hover { background: var(--vz-tertiary-bg); color: var(--vz-primary); }

.composer-send-btn {
  background: var(--vz-primary); border-color: var(--vz-primary); color: #fff;
  height: 40px; width: 40px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0; font-size: 1.05rem; line-height: 1;
}
.composer-send-btn i { line-height: 1; }
.composer-send-btn:hover:not(:disabled) { background: var(--brand-primary-hover, #7a0e14); color: #fff; }
.composer-send-btn:disabled { opacity: 0.5; }

/* Prévia de link ao vivo no composer */
.composer-linkprev {
  display: flex; align-items: stretch; gap: 0.6rem;
  margin: 0.5rem 0.75rem 0; padding: 0.5rem;
  background: var(--vz-tertiary-bg);
  border: 1px solid var(--vz-border-color);
  border-left: 3px solid var(--vz-primary);
  border-radius: 0.5rem; position: relative;
}
.composer-linkprev-load {
  display: flex; align-items: center; gap: 0.5rem;
  color: var(--vz-secondary-color); font-size: 0.85rem; padding: 0.25rem 0.15rem;
}
.composer-linkprev-thumb {
  width: 56px; height: 56px; object-fit: cover; border-radius: 0.375rem; flex-shrink: 0;
}
.composer-linkprev-body { min-width: 0; flex: 1; padding-right: 1.25rem; }
.composer-linkprev-title { font-weight: 600; font-size: 0.85rem; color: var(--vz-body-color); }
.composer-linkprev-desc {
  font-size: 0.78rem; color: var(--vz-secondary-color);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.composer-linkprev-url { font-size: 0.72rem; color: var(--vz-secondary-color); text-transform: lowercase; }
.composer-linkprev-close {
  position: absolute; top: 4px; right: 4px;
  width: 24px; height: 24px; border: 0; border-radius: 50%;
  background: transparent; color: var(--vz-secondary-color);
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1rem;
}
.composer-linkprev-close:hover { background: var(--vz-border-color); color: var(--vz-body-color); }

.preview-anexo { padding: 0.6rem 0.75rem 0; }
.preview-anexo-card {
  display: flex; align-items: center; gap: 0.75rem; position: relative;
  background: var(--vz-tertiary-bg); border: 1px solid var(--vz-border-color);
  border-radius: 0.75rem; padding: 0.6rem; padding-right: 2.2rem;
}
.preview-thumb { width: 60px; height: 60px; object-fit: cover; border-radius: 0.55rem; flex-shrink: 0; background: var(--vz-body-bg); }
.preview-thumb--file {
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.7rem; color: var(--vz-primary); border: 1px solid var(--vz-border-color);
}
.preview-anexo-body { min-width: 0; flex: 1; }
.preview-anexo-nome { font-weight: 600; font-size: 0.86rem; color: var(--vz-body-color); }
.preview-anexo-meta { font-size: 0.72rem; color: var(--vz-secondary-color); margin-bottom: 0.35rem; }
.preview-anexo-legenda {
  width: 100%; border: 0; border-bottom: 1px solid var(--vz-border-color);
  background: transparent; color: var(--vz-body-color); font-size: 0.82rem;
  padding: 0.15rem 0; outline: none;
}
.preview-anexo-legenda:focus { border-bottom-color: var(--vz-primary); }
.preview-anexo-fechar {
  position: absolute; top: 0.4rem; right: 0.4rem;
  width: 26px; height: 26px; border: 0; border-radius: 50%;
  background: transparent; color: var(--vz-secondary-color);
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1.05rem;
}
.preview-anexo-fechar:hover { background: var(--vz-border-color); color: var(--vz-body-color); }
.preview-anexo-acoes { display: flex; justify-content: flex-end; padding: 0.5rem 0 0.15rem; }
.btn-enviar-anexo {
  display: inline-flex; align-items: center; gap: 0.4rem;
  border: 0; border-radius: 2rem; padding: 0.4rem 1.1rem;
  background: var(--vz-primary); color: #fff; font-size: 0.85rem; font-weight: 500; cursor: pointer;
  transition: filter 0.15s;
}
.btn-enviar-anexo:hover:not(:disabled) { filter: brightness(0.92); }
.btn-enviar-anexo:disabled { opacity: 0.6; cursor: not-allowed; }

.recording-bar { display: flex; align-items: center; gap: 0.6rem; padding: 0.5rem 0.75rem; background: var(--vz-body-bg); min-height: 56px; }
.recording-btn { width: 40px; height: 40px; border-radius: 50%; border: 0; display: inline-flex; align-items: center; justify-content: center; font-size: 1.25rem; cursor: pointer; flex-shrink: 0; transition: background 0.15s, color 0.15s; }
.recording-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.recording-btn--ghost { background: transparent; color: var(--vz-secondary-color); }
.recording-btn--ghost:hover:not(:disabled) { background: var(--vz-tertiary-bg); color: var(--vz-body-color); }
.recording-btn--send { background: var(--vz-primary); color: #fff; }
.recording-btn--send:hover:not(:disabled) { filter: brightness(0.92); }

.recording-meta { display: inline-flex; align-items: center; gap: 0.4rem; flex-shrink: 0; min-width: 64px; }
.recording-time { font-size: 0.875rem; color: var(--vz-body-color); font-variant-numeric: tabular-nums; font-weight: 500; }
.rec-dot { width: 10px; height: 10px; background: #dc3545; border-radius: 50%; animation: rec-pulse 1.2s ease-in-out infinite; flex-shrink: 0; }
.rec-dot--pausado { animation: none; opacity: 0.5; }
@keyframes rec-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.45; transform: scale(0.85); } }

.recording-waveform { display: flex; align-items: center; flex-grow: 1; height: 28px; gap: 3px; overflow: hidden; min-width: 0; }
.recording-waveform span { display: block; width: 2px; height: 4px; background: var(--vz-secondary-color); border-radius: 2px; flex-shrink: 0; opacity: 0.55; }
.recording-waveform--ativo span { background: var(--vz-primary); opacity: 1; animation: rec-wave 1.1s ease-in-out infinite; }
@keyframes rec-wave {
  0%, 100% { height: 4px; }
  20%      { height: 14px; }
  40%      { height: 22px; }
  60%      { height: 10px; }
  80%      { height: 18px; }
}

.emoji-picker {
  position: absolute; bottom: calc(100% + 6px); left: 0.5rem;
  background: var(--vz-body-bg); border: 1px solid var(--vz-border-color);
  border-radius: 0.5rem; padding: 0.5rem;
  display: grid; grid-template-columns: repeat(8, 1fr); gap: 0.15rem;
  max-width: 280px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 5;
}
.emoji-btn { background: transparent; border: 0; font-size: 1.25rem; cursor: pointer; padding: 0.2rem; border-radius: 0.25rem; transition: background 0.1s; }
.emoji-btn:hover { background: var(--vz-tertiary-bg); }

.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

[data-bs-theme="dark"] .composer-wrapper,
[data-bs-theme="dark"] .composer,
[data-bs-theme="dark"] .composer-wa { background: #14181d; }
[data-bs-theme="dark"] .composer-input { background: #2a3038; border-color: #3a4049; color: #e6e8eb; }
[data-bs-theme="dark"] .composer-input:focus { border-color: #4a525c; }
[data-bs-theme="dark"] .composer-input:disabled { background: #181c22; color: #7a8089; }
[data-bs-theme="dark"] .composer-action-btn { color: #b8bdc4; }
[data-bs-theme="dark"] .composer-action-btn:hover:not(:disabled) { background: #1f252c; color: #ff9aa5; }
[data-bs-theme="dark"] .composer-popup { background: #1c2128; border-color: #2a3038; box-shadow: 0 6px 24px rgba(0,0,0,0.5); }
[data-bs-theme="dark"] .popup-item { color: #e6e8eb; }
[data-bs-theme="dark"] .popup-item:hover:not(:disabled) { background: #262c34; }
[data-bs-theme="dark"] .popup-divisor { background: #2a3038; }
[data-bs-theme="dark"] .popup-formato { color: #b8bdc4; }
[data-bs-theme="dark"] .popup-formato:hover { background: #262c34; color: #ff9aa5; }
[data-bs-theme="dark"] .emoji-picker { background: #1c2128; border-color: #2a3038; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
[data-bs-theme="dark"] .emoji-btn:hover { background: #262c34; }
[data-bs-theme="dark"] .preview-anexo-card { background: #1c2128; border-color: #2a3038; }
[data-bs-theme="dark"] .preview-anexo-fechar:hover { background: #2a3038; color: #e6e8eb; }
[data-bs-theme="dark"] .recording-bar { background: #14181d; }
[data-bs-theme="dark"] .composer-linkprev { background: #1c2128; border-color: #2a3038; }
[data-bs-theme="dark"] .composer-linkprev-close:hover { background: #2a3038; color: #e6e8eb; }
</style>
